import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageContainer, Skeleton, ErrorBoundary, useToast } from '@bbpos/ui';
import { useSalesOrder, useCreateDelivery, SalesOrderStatusBadge, parseStockShortfalls, formatQty } from '@/features/sales-orders';
import type { StockShortfall } from '@/features/sales-orders';
import { TruckIcon } from '../purchase-orders/icons';
import { LineQuantityForm } from './LineQuantityForm';

function SalesOrderDeliverContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: order, isLoading, error } = useSalesOrder(id);
  const deliverMutation = useCreateDelivery();
  const [shortfalls, setShortfalls] = useState<StockShortfall[] | null>(null);

  if (error) {
    toast({ title: (error as Error).message, variant: 'error' });
    navigate('/sales-orders');
    return null;
  }
  if (isLoading || !order) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-2xl mb-6" />
        <Skeleton variant="rect" height={420} className="rounded-2xl" />
      </>
    );
  }
  if (order.status !== 'CONFIRMED' && order.status !== 'PARTIALLY_DELIVERED') {
    toast({ title: `Cannot record a delivery — order is ${order.status.toLowerCase().replace('_', ' ')}`, variant: 'error' });
    navigate(`/sales-orders/${id}`);
    return null;
  }

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-500 to-indigo-600 p-6 text-white shadow-[0_24px_60px_-24px_rgba(37,99,235,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-white/15 p-2 backdrop-blur-sm ring-1 ring-white/20"><TruckIcon className="h-5 w-5" /></div>
              <h1 className="text-2xl font-bold tracking-tight text-white">Record delivery</h1>
            </div>
            <p className="text-brand-100 mt-1 mono-num">
              {order.so_number} <span className="text-brand-200/70">·</span> {order.customer_name}
              {order.shipping_address && <span className="text-brand-200/70"> · {order.shipping_address}</span>}
            </p>
          </div>
          <SalesOrderStatusBadge status={order.status} size="md" />
        </div>
      </div>

      <LineQuantityForm
        mode="deliver"
        order={order}
        submitting={deliverMutation.isPending}
        shortfalls={shortfalls}
        onBack={() => navigate(`/sales-orders/${id}`)}
        onSubmit={(items, meta) => {
          if (!id) return;
          setShortfalls(null);
          deliverMutation.mutate(
            { id, payload: { items, delivered_date: meta.date || undefined, reference: meta.reference || undefined, notes: meta.notes || undefined } },
            {
              onSuccess: (d) => {
                const units = items.reduce((s, i) => s + i.quantity, 0);
                toast({ title: `Delivery ${d.delivery_number} recorded · ${formatQty(units)} units`, variant: 'success' });
                navigate(`/sales-orders/${id}`);
              },
              onError: (err: Error) => {
                const list = parseStockShortfalls(err);
                if (list) {
                  setShortfalls(list);
                  toast({ title: 'Not enough stock for this delivery', variant: 'warning' });
                } else {
                  toast({ title: err.message, variant: 'error' });
                }
              },
            },
          );
        }}
      />
    </>
  );
}

export function SalesOrderDeliverPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <PageContainer
      title="Record delivery"
      breadcrumbs={[
        { label: 'Sales Orders', href: '/sales-orders' },
        { label: id?.slice(0, 8).toUpperCase() ?? '', href: `/sales-orders/${id}` },
        { label: 'Deliver' },
      ]}
    >
      <ErrorBoundary>
        <SalesOrderDeliverContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
